import {
    Alert, Box,
    Button,
    DialogActions,
    DialogContent,
    TextField, Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import React, {useCallback, useEffect, useState} from "react";
import {campaignServices} from "../../service/campaignService";
import TimezoneSelect from "../timezone/TimezoneSelect";
import {useForm} from "react-hook-form";
import CurrencySelect from "../offers/CurrencySelect";
import Spinner from "../spinner/Spinner";
import moment from 'moment-timezone';
import {connect} from "react-redux";
import {campaignActions} from "../../store/actions/campaignsAction";
import AirbnbDateRangePicker from "../utils/AirbnbDateRangePicker";
import {PaymentsOutlined} from '@mui/icons-material';
import {CampaignDialog, CampaignDialogHeader, CampaignSection} from './CampaignDesign';
import {offerSelectPaperStyles} from '../offers/OfferDesign';

export const CampaignCost = (props) => {

    const {auth: {userSettings: {timezone}}} = props;
    const theme = useTheme();
    const [open, setOpen] = useState(true);
    const {handleSubmit, register, control, setValue, formState: {errors}} = useForm();

    const handleClose = useCallback((event, reason) => {
        if (reason === 'backdropClick') {
            return false;
        }
        setOpen(false);
        props.history.push({pathname: "/campaigns", state: {from: props.location}});
    }, [props]);

    const [loading, setLoading] = useState(false);
    const [campaign, setCampaign] = useState(null);
    const [from, setFrom] = useState(moment().tz(timezone).startOf("d").format("YYYY-MM-DD HH:mm:ss"));
    const [to, setTo] = useState(moment().tz(timezone).endOf("d").format("YYYY-MM-DD HH:mm:ss"));
    const [dateLabel, setDateLabel] = useState("Today");
    const [errorMsg, setErrorMsg] = useState("");

    useEffect(() => {
        async function fetchData(campaignId): void {
            try {
                setLoading(true);
                const campaignResponse = await campaignServices.find(campaignId);
                setCampaign(campaignResponse);
            } catch (e) {
                setErrorMsg(e?.response?.data?.error || 'Unable to load campaign details. Please try again.');
            } finally {
                setLoading(false);
            }
        }

        if (props?.match?.params?.id && campaign === null) {
            fetchData(props?.match?.params?.id);
        }
    }, [campaign, props.match]);

    const onSubmit = (data) => {
        let formData = {
            campaignId: campaign?.id,
            from: from,
            to: to,
            timezone: data?.timezone?.timezone,
            currencyCode: data?.currency?.code,
            cost: parseFloat(data?.cost)
        };

        async function updateCost(formData): void {
            try {
                setLoading(true);
                setErrorMsg("");
                await campaignServices.updateCost(formData);
                handleClose();
                props.rerender();
            } catch (e) {
                setErrorMsg(e?.response?.data?.error || 'Unable to update campaign cost. Please try again.');
            } finally {
                setLoading(false);
            }
        }

        updateCost(formData);
    };

    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <Box width="100%">
            <CampaignDialog open={open} maxWidth="sm"
                    onClose={handleClose}
                    fullWidth
                    fullScreen={fullScreen}
                    aria-labelledby="campaign-cost-title"
            >
                <CampaignDialogHeader id="campaign-cost-title" title="Update cost manually" icon={PaymentsOutlined}
                    description={campaign?.fullName || 'Set the campaign cost for a selected reporting period.'} onClose={handleClose}/>
                <DialogContent>
                    {loading && <Spinner size={32} overComponentBox={true}/>}

                    {errorMsg !== "" && <Alert severity="error" sx={{mb: 2}}>{errorMsg}</Alert>}

                    <Box component="form" id="campaign-cost-form" onSubmit={handleSubmit(onSubmit)} sx={{display: 'grid', gap: 2}}>
                        <CampaignSection title="Reporting period" description="Choose the dates and timezone for this cost update.">
                            <Box sx={{display: 'grid', gap: 2, minWidth: 0}}>
                                <Box><AirbnbDateRangePicker onChangeDate={(from, to, label) => {
                                    setFrom(from); setTo(to); setDateLabel(label);
                                }} dateLabel={dateLabel} from={from} to={to} hideTimezoneSelect compact hideDayNavigation/></Box>
                                <TimezoneSelect setValue={setValue} control={control} selectedValue={timezone} inputLabelShrink
                                    slotProps={{paper: {sx: offerSelectPaperStyles}}}/>
                            </Box>
                        </CampaignSection>
                        <CampaignSection title="Cost details" description="Enter the total cost for the selected period.">
                            <Box sx={{display: 'grid', gridTemplateColumns: {xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))'}, gap: 2}}>
                                <CurrencySelect control={control}/>
                                <TextField error={!!errors?.cost} variant="outlined" id="cost"
                                    {...register('cost', {required: 'Enter a cost.', pattern: {value: /^(\d+)(?:[.]\d+)?$/, message: 'Enter a valid positive amount or zero.'}})}
                                    name="cost" size="small" label="Total cost" placeholder="0.00" type="text" fullWidth
                                    helperText={errors?.cost?.message} slotProps={{inputLabel: {shrink: true}, htmlInput: {inputMode: 'decimal'}}}/>
                            </Box>
                        </CampaignSection>
                        <Typography sx={{fontSize: 12, color: 'text.secondary', lineHeight: 1.6}}>The selected currency applies to this cost update.</Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="inherit" disabled={loading}>
                        Cancel
                    </Button>
                    <Button type="submit" form="campaign-cost-form" disabled={loading || !campaign} color="primary" variant="contained">
                        Update cost
                    </Button>
                </DialogActions>
            </CampaignDialog>
        </Box>
    );
};

const mapStateToProps = (state) => {
    return {
        auth: state.auth
    }
};

const actionCreators = {
    rerender: campaignActions.rerender
};

export default connect(mapStateToProps, actionCreators)(CampaignCost);